import { PointsDefault } from '@/config/chain/types'
import { SWAP_VAULT_CONTRACT } from '@/config/consts'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenAddresses } from '@/utils/tokens'

export enum VaultType {
  NUCLEUS = 0,
  HYPERBEAT = 1,
  EEARN = 2,
}

export const WITHDRAW_PERIOD_ONE_DAY_SECONDS = 86400

export interface VaultBaseMetadata {
  type: VaultType
  vaultToken: Token
  description: {
    short: string
    long: string
    risk: string
    managementFee?: string
  }
  swapVaultContractAddress?: CheckedAddress
  depositTokens: { token: Token; swapPath: `0x${string}` }[]
  chainId: number
  points: PointsDefault
  supportTokens: Token[]
  contracts: {
    baseTokenAddress: CheckedAddress
  }
  withdraw: {
    periodMinSeconds?: number
    periodMaxSeconds: number
    transactionDeadline?: number
  }
  maxSupply?: number
  protocol: {
    owner?: string
    name: string
    strategyProvider: string
    strategyProviderUrl: string
  }
  deprecated?: boolean
}

export interface VaultNucleustMetadata extends VaultBaseMetadata {
  type: VaultType.NUCLEUS
  fee: {
    management: number
    performance: number
  }
  contracts: {
    baseTokenAddress: CheckedAddress
    tellerVaultAddress: CheckedAddress
    atomicQueueVaultAddress: CheckedAddress
    accountantVaultAddress: CheckedAddress
    isTellerBouncerVault?: boolean
  }
}

export interface VaultHyperbeatMetadata extends VaultBaseMetadata {
  type: VaultType.HYPERBEAT
  apy: {
    total: Percentage
    user: Percentage
  }
  fee: {
    management: number
    performance: number
  }
}

export const VAULT_ADDRESSES = ['0x9992ed1214ea2bc91b0587b37c3e03d5e2a242c1'] as const

export const VAULTS_METADATA: Record<string, VaultNucleustMetadata | VaultHyperbeatMetadata> = {
  '0x9992ed1214ea2bc91b0587b37c3e03d5e2a242c1': {
    type: VaultType.NUCLEUS,
    vaultToken: TokenAddresses.stkUSDXL,
    depositTokens: [
      {
        token: TokenAddresses.USDXL,
        swapPath: '0x0',
      },
    ],
    supportTokens: [TokenAddresses.USDXL],
    fee: {
      management: 0,
      performance: 0,
    },
    contracts: {
      baseTokenAddress: CheckedAddress('0xca79db4B49f608eF54a5CB813FbEd3a6387bC645'),
      tellerVaultAddress: CheckedAddress('0xD1A252b24C1E303b31c66b2Fff0AEBb8b05FF657'),
      atomicQueueVaultAddress: CheckedAddress('0x228C44Bb4885C6633F4b6C83f14622f37D5112E5'),
      accountantVaultAddress: CheckedAddress('0x4854E110E9B7e3b87596f5F1f1A9dCcAa0568D9D'),
      isTellerBouncerVault: true,
    },
    chainId: 999,
    protocol: {
      owner: 'Last',
      name: 'USDXL',
      strategyProvider: 'Nucleus Labs',
      strategyProviderUrl: 'https://www.nucleusearn.io/',
    },
    points: {
      multiplier: 2,
    },
    withdraw: {
      periodMaxSeconds: WITHDRAW_PERIOD_ONE_DAY_SECONDS * 3,
      transactionDeadline: WITHDRAW_PERIOD_ONE_DAY_SECONDS * 7,
    },
    description: {
      short: 'Earn yield on your USDXL',
      long: '',
      risk: '',
    },
    deprecated: true,
  },
  '0x4fdc8bc634a3ac7911e7edf1f31812891f41772d': {
    type: VaultType.NUCLEUS,
    vaultToken: TokenAddresses.XLHYPE,
    swapVaultContractAddress: SWAP_VAULT_CONTRACT,
    depositTokens: [
      {
        token: TokenAddresses.HYPE,
        swapPath: '0x5555555555555555555555555555555555555555000000c85748ae796ae46a4f1348a1693de4b50560485562',
      },
      {
        token: TokenAddresses.WHYPE,
        swapPath: '0x5555555555555555555555555555555555555555000000c85748ae796ae46a4f1348a1693de4b50560485562',
      },
      {
        token: TokenAddresses.USDXL,
        swapPath:
          '0xca79db4b49f608ef54a5cb813fbed3a6387bc645000009c45555555555555555555555555555555555555555000000c85748ae796ae46a4f1348a1693de4b50560485562',
      },
      {
        token: TokenAddresses.LHYPE,
        swapPath: '0x0',
      },
    ],
    supportTokens: [TokenAddresses.LHYPE],
    fee: {
      management: 0,
      performance: 0,
    },
    contracts: {
      baseTokenAddress: CheckedAddress('0x5555555555555555555555555555555555555555'),
      tellerVaultAddress: CheckedAddress('0xdd558db2b6a9109367f08c2a570290bdaa115958'),
      atomicQueueVaultAddress: CheckedAddress('0x228C44Bb4885C6633F4b6C83f14622f37D5112E5'),
      accountantVaultAddress: CheckedAddress('0xa17ff0a1e1acc5de4921f7fa177540a504eddfe4'),
    },
    chainId: 999,
    protocol: {
      name: 'Nucleus Labs',
      strategyProvider: 'Nucleus Labs',
      strategyProviderUrl: 'https://www.nucleusearn.io/',
    },
    points: {
      multiplier: 1,
    },
    withdraw: {
      periodMinSeconds: WITHDRAW_PERIOD_ONE_DAY_SECONDS * 1,
      periodMaxSeconds: WITHDRAW_PERIOD_ONE_DAY_SECONDS * 5,
    },
    description: {
      short:
        'Maximize staked HYPE yield through a looped strategy that deposits stHYPE and borrows HYPE, stakes it and redeposits',
      long: 'Maximize staked HYPE yield through a looped strategy that deposits stHYPE and borrows HYPE, stakes it and redeposits. Maximize staked HYPE yield through a looped strategy that deposits stHYPE and borrows HYPE, stakes it and redeposits',
      risk: 'Maximize staked HYPE yield through a looped strategy that deposits stHYPE and borrows HYPE, stakes it and redeposits. Maximize staked HYPE yield through a looped strategy that deposits stHYPE and borrows HYPE, stakes it and redeposits',
    },
  },
  '0x96c6cbb6251ee1c257b2162ca0f39aa5fa44b1fb': {
    type: VaultType.HYPERBEAT,
    vaultToken: TokenAddresses.hbHYPE,
    depositTokens: [
      {
        token: TokenAddresses.WHYPE,
        swapPath: '0x',
      },
    ],
    supportTokens: [TokenAddresses.WHYPE],
    contracts: {
      baseTokenAddress: CheckedAddress('0x5555555555555555555555555555555555555555'),
    },
    fee: {
      management: 0,
      performance: 0,
    },
    chainId: 999,
    protocol: {
      name: 'Hyperbeat',
      strategyProvider: 'Hyperbeat',
      strategyProviderUrl: 'https://app.hyperbeat.org/vaults/hype',
    },
    points: {
      multiplier: 1,
    },
    apy: {
      total: Percentage(0.06),
      user: Percentage(0.06),
    },
    withdraw: {
      periodMinSeconds: WITHDRAW_PERIOD_ONE_DAY_SECONDS * 1,
      periodMaxSeconds: WITHDRAW_PERIOD_ONE_DAY_SECONDS * 2,
    },
    description: {
      short:
        'The best risk-adjusted return on your HYPE, dynamically allocating capital through strategies typically exclusive to institutions now democratised for all',
      long: 'The Hyperbeat Ultra HYPE vault is an automated DeFi strategy vault that provides users a simple access point to use their HYPE in the HyperEVM ecosystem. Assets are deployed to generate real yield and earn exclusive incentives from partners.',
      risk: 'Liquid vaults carry smart contract risks along with varying levels of economic risk. This particular vault engages in liquidity provision, which may lead to impermanent loss.',
      managementFee:
        'Zero platform fee for the first 30-days, thereafter a 2% platform fee will be charged annually on your deposited amount and prorated based on the duration your funds remain in the vault.',
    },
  },
}
